import { clearAllLocFor } from "@/models/localForage/localForage.model";
import {
  ChatMessage,
  chatMessageSchema,
  Conversation,
  conversationSchema,
} from "@/types";
import { validateArrayWithSchema } from "@/utils/dataValidators.zod";
import localforage from "localforage";
import { z } from "zod";

const localForageConversationMessagesSchema = z.object({
  conversationID: z.number(),
  messages: z.array(chatMessageSchema),
});

type LocalForageConversationMessages = z.infer<
  typeof localForageConversationMessagesSchema
>;

export async function locForSetConversations(
  conversations: Conversation[]
): Promise<void> {
  await localforage.setItem("conversations", conversations);
}

export async function locForGetConversations(): Promise<Conversation[] | null> {
  const localforageData = await localforage.getItem("conversations");
  if (!localforageData) {
    return null;
  }

  const validatedData = validateArrayWithSchema<Conversation>(
    localforageData,
    conversationSchema,
    "Conversation"
  );

  if (validatedData) {
    return validatedData;
  }

  console.error(
    "locForGetConversations: Invalid conversations",
    localforageData
  );
  await clearAllLocFor();
  window.location.reload();
  return null;
}

export async function locForGetConversationsMessages(
  conversationID: number
): Promise<ChatMessage[] | null> {
  const localforageData = await localforage.getItem("conversations-messages");
  if (!localforageData) {
    return null;
  }
  const validatedData =
    validateArrayWithSchema<LocalForageConversationMessages>(
      localforageData,
      localForageConversationMessagesSchema,
      "LocalForageConversationMessages"
    );

  if (validatedData) {
    const conversationMessages = validatedData.find(
      (conversation) => conversation.conversationID === conversationID
    );
    if (conversationMessages) {
      return conversationMessages.messages;
    } else {
      console.warn(
        "locForGetConversationsMessages: Conversation not found",
        conversationID
      );
      return null;
    }
  }

  console.error(
    "locForGetConversationsMessages: Invalid conversations-messages",
    localforageData
  );
  await clearAllLocFor();
  window.location.reload();
  return null;
}

export async function locForSetConversationsMessages(
  conversationID: number,
  messages: ChatMessage[]
): Promise<void> {
  const localforageData = await localforage.getItem("conversations-messages");

  if (!localforageData) {
    await localforage.setItem("conversations-messages", [
      {
        conversationID: conversationID,
        messages: messages,
      },
    ]);
    return;
  }

  // check if the data is valid
  const validatedData =
    validateArrayWithSchema<LocalForageConversationMessages>(
      localforageData,
      localForageConversationMessagesSchema,
      "LocalForageConversationMessages"
    );

  if (validatedData) {
    const conversationMessages = validatedData.find(
      (conversation) => conversation.conversationID === conversationID
    );
    if (conversationMessages) {
      conversationMessages.messages = messages;
    } else {
      validatedData.push({
        conversationID: conversationID,
        messages: messages,
      });
    }
  } else {
    console.error(
      "locForSetConversationsMessages: Invalid conversations-messages",
      localforageData
    );
    await clearAllLocFor();
    window.location.reload();
  }

  await localforage.setItem("conversations-messages", validatedData);
}
